import React from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Persistor } from 'redux-persist';
import { EMERGENCY_DATA } from '@rsos/constants/appNames';
import useHasAccessToHarmonyPortalChat from '@rsos/hooks/useHasAccessToHarmonyPortalChat';
import { selectIsJVEnabled } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { selectLoadingSaml } from '@rsos/sinatra/src/selectors/auth';
import { selectUser } from '@rsos/sinatra/src/selectors/user';
import { readMetaTag } from '@rsos/utils/metaTags';
import onSAMLrequest from '@rsos/utils/onSAMLrequest';
import useIsIRP from '@rsos/utils/useIsIRP';
import AskHarmonyBtn from '../AskHarmonyBtn';
import NavigationControl from '../NavigationControl/NavigationControl';
import ResourceRequestBtn from '../ResourceRequestBtn';
import SettingsControl from '../SettingsControl';
import TrainingBtn from '../TrainingBtn';
import ButtonGroup from './ButtonGroup';
import './RightHandButtonNav.css';

interface RightHandButtonNavProps {
  persistor: Persistor;
  path: string;
  handleLogout: unknown;
}

function RightHandButtonNav({
  persistor,
  path,
  handleLogout,
}: RightHandButtonNavProps): React.ReactElement {
  const dispatch = useDispatch();
  const feAppName = readMetaTag('fe-app-name');
  // Shared capstone nav but for ResourceRequestBtn we need resourceRequests and api,
  // which is only in emergency_data.
  const showResourceRequestBtn = feAppName === EMERGENCY_DATA;
  const hasAccessToHarmonyPortalChat = useHasAccessToHarmonyPortalChat();

  // Properties for TrainingBtn
  const loadingSAML = useSelector(selectLoadingSaml);
  const handleSAMLRequest = (service: unknown) => {
    return onSAMLrequest(service, dispatch, loadingSAML, persistor);
  };

  // Properties for NavigationControl
  const user = useSelector(selectUser);
  const { irp_access_token } = useIsIRP();
  const isJVenabled = useSelector(selectIsJVEnabled);

  return (
    <nav className="right-hand-button-nav">
      <ButtonGroup data-name="tool-button-group">
        {showResourceRequestBtn && <ResourceRequestBtn />}
      </ButtonGroup>
      {hasAccessToHarmonyPortalChat && (
        <ButtonGroup data-name="ask-harmony-group">
          <AskHarmonyBtn />
        </ButtonGroup>
      )}
      <ButtonGroup data-name="training-button-group">
        <TrainingBtn handleSAMLRequest={handleSAMLRequest} />
      </ButtonGroup>
      <ButtonGroup data-name="settings-and-nav-container">
        <SettingsControl path={path} />
        <NavigationControl
          user={user}
          handleSAMLRequest={handleSAMLRequest}
          hasNewDataSource={user.hasNewDataSource}
          isLoadingSAML={loadingSAML}
          handleLogout={handleLogout}
          irpAccessToken={irp_access_token}
          isJVenabled={isJVenabled}
          data-name="navigation-navigation-control"
        />
      </ButtonGroup>
    </nav>
  );
}

export default RightHandButtonNav;
